import React, { useCallback, useContext, useState } from 'react';
import { UserContext } from '../UserContext';
import TabIntro from '../../Common/Form/Tab/TabIntro';
import { Button, ErrorMessage, InputGroup, Label, TextField, validationRules, extractErrors } from '../../Common/Form';
import { object, string } from 'yup';
import { TabContext } from '../../Common/Form/Tab/TabContext';
import Feedback from '../Feedback';
import useMainContent from '../../../hooks/useMainContent';

const { email, confirmPassword, currentPassword } = validationRules;

const validationSchema = object({
    email,
    password: string()
        .notRequired()
        .test('empty-or-min', 'The New Password field must be at least 8 characters', (value) => {
            if (value.length === 0) {
                return true;
            }

            return value.length >= 8;
        })
        .max(64, 'The New Password field must be less than 64 characters'),
    confirmPassword,
    currentPassword,
});

const EmailAndPasswordForm = () => {
    const { user } = useContext(UserContext);
    const { setTabTouched, mustSave } = useContext(TabContext);

    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [globalError, setGlobalError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (user !== null && Object.entries(values).length === 0) {
        setValues({
            email: user.email.split('@')[0],
            currentPassword: '',
            password: '',
            confirmPassword: '',
        });
    }

    const scrollToMainContent = useMainContent();

    const onSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setIsSubmitting(true);

            const formData = new FormData(e.currentTarget);
            formData.set('email', `${formData.get('email')}@${user.email.split('@')[1]}`);

            const data = Object.fromEntries(formData.entries());

            await validationSchema
                .validate(data, { strict: true, abortEarly: false })
                .then(() => {
                    if (!data.password) {
                        delete data.confirmPassword;
                    } else {
                        data.newPassword = data.password;
                    }

                    data.password = data.currentPassword;
                    delete data.currentPassword;

                    data.userId = user.userId;
                    data.action = 'users/save-user';

                    fetch('/', {
                        method: 'POST',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/x-www-form-urlencoded',
                            'X-Requested-With': 'XMLHttpRequest',
                            'X-CSRF-Token': window.csrfTokenValue,
                        },
                        body: new URLSearchParams(data),
                    })
                        .then((r) => {
                            return r.json();
                        })
                        .then((d) => {
                            if (typeof d.errors?.currentPassword[0] !== 'undefined') {
                                setErrors((prev) =>
                                    Object.assign(prev, { currentPassword: d.errors?.currentPassword[0] })
                                );
                                setIsSubmitting(false);

                                return;
                            }

                            setErrors({});

                            setValues(
                                Object.assign(values, {
                                    currentPassword: '',
                                    password: '',
                                    confirmPassword: '',
                                })
                            );

                            setTabTouched(false);
                            setSuccess(true);
                            setIsSubmitting(false);
                        })
                        .catch(() => {
                            setGlobalError(true);
                        });
                })
                .catch((errs) => {
                    setErrors(extractErrors(errs));
                    setIsSubmitting(false);
                })
                .finally(() => {
                    scrollToMainContent();
                });
        },
        [
            user,
            setTabTouched,
            setSuccess,
            setErrors,
            setGlobalError,
            setIsSubmitting,
            values,
            setValues,
            scrollToMainContent,
        ]
    );

    if (Object.entries(values).length === 0) {
        return null;
    }

    return (
        <>
            <TabIntro text="">
                <h3>Change Your Email and Password</h3>
            </TabIntro>

            <Feedback
                errors={errors}
                success={success}
                globalError={globalError}
                mustSave={mustSave}
                setSuccess={setSuccess}
            />

            <form onInput={() => setTabTouched(true)} onSubmit={onSubmit}>
                <fieldset>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="note">
                        <span className="required">*</span> Indicates required field
                    </label>

                    <div className="grid -thirdReverse -form">
                        <div className="grid_cell">
                            <Label id="email" text="Corporate Email" required />
                            <InputGroup
                                id="email"
                                label="Corporate Email"
                                required
                                reverse
                                value={values.email}
                                text={`@${user.email.split('@')[1]}`}
                            />
                            <ErrorMessage message={errors.email ?? ''} />
                        </div>
                    </div>

                    <div className="grid -col3 -form">
                        <div className="grid__cell">
                            <TextField
                                id="currentPassword"
                                label="Current Password"
                                required
                                managed
                                value={values.currentPassword}
                                onChange={(e) => {
                                    setValues({ ...values, currentPassword: e.target.value });
                                }}
                                type="password"
                            />
                            <ErrorMessage message={errors.currentPassword ?? ''} />
                        </div>
                    </div>

                    <div className="grid -col3 -form">
                        <div className="grid__cell">
                            <TextField
                                id="password"
                                label="New Password"
                                required={false}
                                managed
                                value={values.password}
                                onChange={(e) => {
                                    setValues({ ...values, password: e.target.value });
                                }}
                                type="password"
                            />
                            <ErrorMessage message={errors.password ?? ''} />
                        </div>
                    </div>

                    <div className="grid -col3 -form">
                        <div className="grid__cell">
                            <TextField
                                id="confirmPassword"
                                label="Confirm Password"
                                required={false}
                                managed
                                value={values.confirmPassword}
                                onChange={(e) => {
                                    setValues({ ...values, confirmPassword: e.target.value });
                                }}
                                type="password"
                            />
                            <ErrorMessage message={errors.confirmPassword ?? ''} />
                        </div>
                    </div>

                    <div className="form__actions">
                        <Button text="Update Information" className="-cta" disabled={isSubmitting} />
                    </div>
                </fieldset>
            </form>
        </>
    );
};

EmailAndPasswordForm.propTypes = {};

export default EmailAndPasswordForm;
